import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { EVENT_STATUS } from 'utils/constants/status';
import { LOCATION_TYPE_LIST } from 'utils/constants/lists';
import { getFeatureAvailability, FEATURES } from 'utils/featureToggle';
import { icons } from 'utils/common';
import LocationIcon from 'views/components/icons/eventIcons/locationIcon';
import { ICalendarEvent } from 'model/calendar/events';
import { CalendarView } from 'utils/cache/calendar';
import { EVENT_TYPES } from 'utils/constants/appointmentsTypes';
import {
  isSeriesEndingWithin4Weeks,
  isSeriesStartingLater,
  formatSeriesShortDate
} from 'utils/validators/planner';

interface IProps {
  event: ICalendarEvent;
  view: CalendarView;
  provider?: boolean;
  redesign?: boolean;
  style?: any;
}
const Event = ({ event, view, provider, redesign }: IProps) => {
  const {
    type,
    title,
    desc,
    status,
    clinic,
    room,
    start,
    color,
    chipColor,
    locationType,
    locationCategory,
    appointmentType
  } = event;

  const [tip, setTip] = useState<{ x: number; y: number; above: boolean } | null>(
    null
  );

  // ---- Provider profile (redesigned, DT only) renderers ----
  // The grid gives a slot 48px per half hour, so a 30-min bar has room for two
  // text rows and anything longer has room for three.
  const durationMin =
    event.start && event.end
      ? (new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000
      : 0;
  // Only the start time goes on a bar — its height already shows how long the
  // slot runs, and the full range never fits beside a client name.
  const startTimeLabel = event.start
    ? moment(event.start).format('h:mm A')
    : '';
  const isShort = durationMin > 0 && durationMin <= 30;

  if (redesign && type === 'availability') {
    return (
      <div className="provider-avail">
        <span className="avail-label-text">{title}</span>
        {!isShort && startTimeLabel && (
          <span className="avail-time">{startTimeLabel}</span>
        )}
      </div>
    );
  }
  if (redesign && type !== 'na' && type !== 'availability') {
    const isAdmin = appointmentType?.eventType?.name === EVENT_TYPES.Admin;
    const apptTypeLabel = desc?.main || appointmentType?.title;
    const timeRange =
      event.start && event.end
        ? `${moment(event.start).format('h:mm A')} - ${moment(
            event.end
          ).format('h:mm A')}`
        : '';
    // Each row gets its own line, in the order a scheduler needs them:
    //   <=30 min -> client name, start time             (2 rows fit)
    //   > 30 min -> client name, start time, type chip  (3 rows fit)
    const showChip = !isShort;
    const clinicTz =
      clinic?.timezone ||
      (event as any).client?.clinic?.timezone ||
      (event as any).provider?.clinic?.timezone;
    const isEndingSoon = isSeriesEndingWithin4Weeks(
      event.recurrencePattern?.recurringUntil,
      event.recurrencePattern?.recurringEvery,
      clinicTz
    );
    // Confirmed but not started yet: row 2 carries the start date instead of
    // the slot time, since "when does this begin" is the useful fact.
    const isUpcoming = isSeriesStartingLater(event.seriesStartDate, clinicTz);
    const startsOnLabel = isUpcoming
      ? formatSeriesShortDate(event.seriesStartDate, clinicTz)
      : '';
    // A slot about to free up is as useful to a scheduler as one about to fill,
    // so the end date reads the same way the start date does.
    const endsOnLabel = isEndingSoon
      ? formatSeriesShortDate(event.recurrencePattern?.recurringUntil, clinicTz)
      : '';
    // A proposed slot is greyed out, but colour alone does not say why, and it
    // is the one state the session count leaves out — so a scheduler counting
    // bars finds one more than the pill reports. The word carries that.
    const isPending = Boolean(event.isPendingConfirmation);
    const clientId =
      (event as any).client?.id ?? (event as any).clientId ?? null;
    const showTip = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!title && !timeRange && !apptTypeLabel) return;
      const r = e.currentTarget.getBoundingClientRect();
      const above = window.innerHeight - r.bottom < 64;
      setTip({ x: r.left + r.width / 2, y: above ? r.top : r.bottom, above });
    };
    // Supplementary only — everything here is already on the bar, except the
    // type chip on short bars and names long enough to be truncated.
    const renderTip = () =>
      tip &&
      createPortal(
        <div
          className="appt-time-tip"
          style={{
            position: 'fixed',
            left: tip.x,
            top: tip.above ? tip.y - 8 : tip.y + 8,
            transform: tip.above
              ? 'translate(-50%, -100%)'
              : 'translateX(-50%)',
            background: '#4a4a4a',
            color: '#ffffff',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            zIndex: 3000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            pointerEvents: 'none'
          }}
        >
          {title && <div style={{ fontWeight: 600 }}>{title}</div>}
          {timeRange && <div>{timeRange}</div>}
          {apptTypeLabel && <div>{apptTypeLabel}</div>}
          {isEndingSoon && <div>Series ending {endsOnLabel}</div>}
          <span
            style={{
              position: 'absolute',
              left: '50%',
              width: '8px',
              height: '8px',
              background: '#4a4a4a',
              transform: 'translateX(-50%) rotate(45deg)',
              ...(tip.above ? { bottom: '-4px' } : { top: '-4px' })
            }}
          />
        </div>,
        document.body
      );
    if (isAdmin) {
      return (
        <div
          className="provider-appt admin"
          onMouseEnter={showTip}
          onMouseLeave={() => setTip(null)}
        >
          {apptTypeLabel && (
            <div className="appt-type-admin">{apptTypeLabel}</div>
          )}
          {renderTip()}
        </div>
      );
    }
    // Client name leads, then the start time, then the appointment type — most
    // clinicians only deliver one therapy, so the type is the least
    // distinguishing thing on the bar. Each gets its own row so nothing has to
    // share width and truncate; the type is what falls off a 30-min bar.
    return (
      <div
        className="provider-appt"
        onMouseEnter={showTip}
        onMouseLeave={() => setTip(null)}
      >
        <div className="appt-headline">
          {title && (
            <span className="appt-name">
              {clientId ? (
                // The day grid had this and the week view did not, which made
                // "can I open this client" depend on which view you were in.
                // Both have it now; a slot with no client stays plain text.
                <a
                  href={`/clients/${clientId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {title}
                </a>
              ) : (
                title
              )}
            </span>
          )}
          {isEndingSoon && (
            <span
              className="ending-dot"
              title={`Series ending ${endsOnLabel}`}
            >
              ●
            </span>
          )}
        </div>
        {isPending ? (
          // Pending outranks starting and ending: a slot that may never be
          // confirmed matters more than when it would have begun or finished.
          <div className="appt-pending">
            <span className="pending-dot">◌</span>
            Pending
            {startTimeLabel && (
              <span className="pending-time">, {startTimeLabel}</span>
            )}
          </div>
        ) : isUpcoming ? (
          <div className="appt-starts">
            <span className="starts-dot">●</span>
            Starts {startsOnLabel}
            {startTimeLabel && (
              <span className="starts-time">, {startTimeLabel}</span>
            )}
          </div>
        ) : isEndingSoon ? (
          // The headline dot (AXON-3581) flags an ending series but not when it
          // ends, which leaves a slot on its way out looking identical to a
          // settled booking. This row supplies the date, reading the way
          // "Starts" does on an incoming slot so the two are told apart at a
          // glance — needed once a rescheduled series puts both on the same day.
          <div className="appt-ending">
            Ending {endsOnLabel}
            {startTimeLabel && (
              <span className="ending-time">, {startTimeLabel}</span>
            )}
          </div>
        ) : (
          startTimeLabel && <div className="appt-time">{startTimeLabel}</div>
        )}
        {showChip && apptTypeLabel && (
          <span
            className="appt-chip"
            style={chipColor ? { backgroundColor: chipColor } : undefined}
          >
            {apptTypeLabel}
          </span>
        )}
        {renderTip()}
      </div>
    );
  }

  const startDate = moment(start).format('hh:mm A');
  const roomSelected = room?.id ? `, ${room?.name || ''}` : '';

  const inClinic =
    locationType === LOCATION_TYPE_LIST[0].id || // OR-ing for cases where events were created via Outlook ('locationType' not set)
    (!locationType && clinic?.id);

  const isTelehealth = getFeatureAvailability(FEATURES.TELEHEALTH)
    ? locationType === LOCATION_TYPE_LIST[2].id
    : false;
  const isRecurringEvent = () => {
    return event?.recurrencePattern?.recurringUntil;
  };

  return (
    <>
      {type !== 'na' && (
        <div>
          <i
            style={{
              backgroundColor: color
            }}
          ></i>
          <p
            style={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap'
            }}
          >
            <strong>
              {view === 'month' && startDate}{' '}
              {view === 'month' &&
              appointmentType?.eventType?.name === EVENT_TYPES.Admin
                ? appointmentType.title
                : title}
            </strong>
          </p>
          {view !== 'month' && (
            <>
              <p
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                {desc?.main}
              </p>
              {view === 'week' &&
                desc?.extras !== undefined &&
                desc?.extras?.map((extra, index) => (
                  <p
                    key={index}
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {extra}
                  </p>
                ))}
              <p
                className="event-room"
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                {inClinic
                  ? `${clinic?.abbreviation}${
                      roomSelected ? `${roomSelected}` : ''
                    }`
                  : isTelehealth
                  ? ''
                  : `${locationCategory}`}
              </p>
              {appointmentType?.eventType?.name !== EVENT_TYPES.Admin &&
                provider && (
                  <p
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isRecurringEvent()
                      ? moment(event?.recurrencePattern?.recurringUntil).format(
                          'MM/DD/YYYY'
                        )
                      : moment(event.endDate).format('MM/DD/YYYY')}
                  </p>
                )}

              <span className="event-status">
                {(icons as Record<string, any>)[status!]}
                <LocationIcon
                  locationType={locationType}
                  className="location-icon"
                />
                {(status === EVENT_STATUS.needsReschedule && 'pending') ||
                  status}
              </span>
            </>
          )}
        </div>
      )}
    </>
  );
};

Event.propTypes = {
  event: PropTypes.object,
  view: PropTypes.string
};

export default Event;
